import { useState, useCallback } from "react";

/**
 * Custom hook for managing partner filtering logic
 * Handles partner selection, checking, and filtering
 */
const usePartnerFiltering = () => {
  const [selectedPartners, setSelectedPartners] = useState([]);
  const [filteredPartners, setFilteredPartners] = useState([]);
  const [checkedPartners, setCheckedPartners] = useState({});

  const handlePartnersLoaded = useCallback((partners) => {
    if (partners.length > 0) {
      const partnerIds = partners.map(partner => partner.id);
      setSelectedPartners(partnerIds);
      setFilteredPartners(partners);
      
      const checked = {};
      partners.forEach(partner => {
        checked[partner.id] = true;
      });
      setCheckedPartners(checked);
      
      return partnerIds;
    }
    return [];
  }, []);

  const handlePartnerSelectionChange = useCallback((selectedPartnersList) => {
    if (!selectedPartnersList || selectedPartnersList.length === 0) {
      setFilteredPartners([]);
      setCheckedPartners({});
      setSelectedPartners([]);
      return;
    }

    const partnerIds = selectedPartnersList.map(p => p.id);
    setSelectedPartners(partnerIds);
    setFilteredPartners(selectedPartnersList);

    const checked = {};
    selectedPartnersList.forEach(partner => {
      checked[partner.id] = true;
    });
    setCheckedPartners(checked);
  }, []);

  const handlePartnerCheck = useCallback((partnerId, isChecked) => {
    setCheckedPartners(prev => ({
      ...prev,
      [partnerId]: isChecked
    }));
  }, []);

  const getSelectedPartnerIds = useCallback(() => {
    const filteredPartnerIds = new Set(filteredPartners.map(p => p.id));
    return Object.keys(checkedPartners)
      .filter(id => {
        const partnerId = parseInt(id);
        return checkedPartners[id] && filteredPartnerIds.has(partnerId);
      })
      .map(id => parseInt(id));
  }, [checkedPartners, filteredPartners]);

  const selectAll = useCallback(() => {
    const checked = {};
    filteredPartners.forEach(partner => {
      checked[partner.id] = true;
    });
    setCheckedPartners(checked);
  }, [filteredPartners]);

  const deselectAll = useCallback(() => {
    const checked = {};
    filteredPartners.forEach(partner => {
      checked[partner.id] = false;
    });
    setCheckedPartners(checked);
  }, [filteredPartners]);

  const resetFiltering = useCallback(() => {
    setSelectedPartners([]);
    setFilteredPartners([]);
    setCheckedPartners({});
  }, []);

  return {
    selectedPartners,
    filteredPartners,
    checkedPartners,
    handlePartnersLoaded,
    handlePartnerSelectionChange,
    handlePartnerCheck,
    getSelectedPartnerIds,
    selectAll,
    deselectAll,
    resetFiltering,
  };
};

export default usePartnerFiltering;
